import { Eye, Loader2, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { RequestCommissionEditDialog } from "@/pages/commission/RequestCommissionEditDialog";
import type { CommissionTransaction } from "@/lib/commission.api";
import type { Party } from "@/lib/party.api";
import { getPartyName } from "./commission-calculations";
import { formatDate, getCommissionAmount } from "./commission-formatters";
import { CommissionPendingBadge } from "./components/CommissionPendingBadge";

export function createCommissionColumns({
  parties,
  typeLabel,
  canDelete,
  deletingId,
  onSelect,
  onReload,
  onDelete,
}: {
  parties: Record<number, Party>;
  typeLabel: (code: string) => string;
  canDelete: boolean;
  deletingId: number | null;
  onSelect: (commission: CommissionTransaction) => void;
  onReload: () => void;
  onDelete: (id: number) => void;
}): ColumnDef<CommissionTransaction, any>[] {
  return [
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => (
        <span className="text-[12px] font-medium text-body">
          {typeLabel(row.original.type)}
        </span>
      ),
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }) => (
        <span className="text-[12px] text-muted-foreground">
          {formatDate(row.original.date)}
        </span>
      ),
    },
    {
      accessorKey: "note",
      header: "Note",
      cell: ({ row }) => (
        <span
          className="block max-w-[220px] truncate text-[12px] text-muted-foreground"
          title={row.original.note}
        >
          {row.original.note || "-"}
        </span>
      ),
    },
    {
      id: "client",
      header: "Client",
      cell: ({ row }) => (
        <span className="text-[12px] text-muted-foreground">
          {row.original.clientPartyId
            ? (getPartyName(parties, row.original.clientPartyId) ?? "...")
            : "-"}
        </span>
      ),
    },
    {
      id: "referrer",
      header: "Référent",
      cell: ({ row }) => (
        <span className="text-[12px] text-muted-foreground">
          {row.original.referrerPartyId
            ? (getPartyName(parties, row.original.referrerPartyId) ?? "...")
            : "-"}
        </span>
      ),
    },
    {
      accessorKey: "commissionAmount",
      header: "Montant",
      meta: { align: "right" },
      cell: ({ row }) => (
        <span className="text-[12px] font-medium text-body">
          {getCommissionAmount(row.original).toLocaleString("fr-FR")}
        </span>
      ),
    },
    {
      id: "editRequest",
      header: "",
      meta: { align: "right" },
      cell: ({ row }) =>
        row.original.pendingEditRequestId ? (
          <CommissionPendingBadge />
        ) : (
          <span onClick={(event) => event.stopPropagation()}>
            <RequestCommissionEditDialog commission={row.original} onRequested={onReload} />
          </span>
        ),
    },
    {
      id: "detail",
      header: "",
      meta: { align: "right" },
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          title="Voir le détail"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(row.original);
          }}
        >
          <Eye size={13} />
        </Button>
      ),
    },
    ...(canDelete
      ? [
          {
            id: "actions",
            header: "",
            meta: { align: "right" as const },
            cell: ({ row }: { row: { original: CommissionTransaction } }) => (
              <Button
                variant="ghost"
                size="icon"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(row.original.id);
                }}
                className="text-danger-text hover:bg-danger-bg"
                title="Supprimer"
                disabled={deletingId === row.original.id}
              >
                {deletingId === row.original.id ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Button>
            ),
          },
        ]
      : []),
  ];
}
